import { useLoadingOverlay } from '@/components'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useOfferGrid } from '@/context/OfferGridContext'
import type { IOffersContext } from '@/context/OffersProvider'
import { usePrintContext } from '@/context/PrintContext'
import { useOffersContext } from '@/context/useOffersContext'
import { useGetGroupedRisks } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useResolveChangedParts } from '@/hooks/useOfferPackage'
import { Box, Checkbox, Flex, Text } from '@chakra-ui/react'
import type { OfferPackage, Variant } from 'BrokerTsLib/src/dtos'
import { PackageItemTypeCategory, Product, Risk } from 'BrokerTsLib/src/enums'
import _ from 'lodash'
import type { icons } from '../icons'
import { changePartLimit, GetPackageItemLimit } from '../OfferCell'
import { OfferCellWrapper } from '../OfferCellWrapper'
import { SectionTitle } from '../SectionTitle'
import { OfferPackageColumnSection } from './OfferPackageColumnSection'

export const OfferPackageHeaderColumn = ({ variant }: { variant: Variant }) => {
  const { insuranceParams } = useInsuranceParams()
  const { openedColumns, onSectionTitleClick, risks } = useOfferGrid()
  const insuranceGroups = useGetGroupedRisks(variant)
  const { product } = useApp()
  const { setLoading } = useLoadingOverlay()
  const { printMode } = usePrintContext()
  const { mutateAsync: resolveParts } = useResolveChangedParts()

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const responsibility = insuranceGroups['0']?.find(x => x.id == Risk.RESPONSIBILITY)
  const responsibilityRealestate = insuranceGroups['0']?.find(x => x.id == Risk.RESPONSIBILITY_REALESTATE)
  const spolProperty = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_PROPERTY)
  const spolFlood = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_FLOOD)
  const spolResponsibility = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_RESPONSIBILITY)

  const responsibilityRiskItem = risks.find(x => x.riskId == Risk.RESPONSIBILITY)
  const responsibilityRealestateRiskItem = risks.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE)

  const { updateOffer, offers } = useOffersContext() as unknown as IOffersContext<OfferPackage>

  const handleToggleAll = (show: boolean, riskId: number) => {
    offers?.forEach(o => {
      const offer = _.cloneDeep(o)
      const parts = offer.parts.filter(x => x.riskId == riskId) ?? []
      if (parts.length == 0) return

      let changed = false
      parts.forEach(part => {
        if (!part.packageItem!.addable)
          return

        const [value, packageItemVariantId] = GetPackageItemLimit(part, part.packageItem!)
        changed = changePartLimit(part, show, value as number, packageItemVariantId as number) || changed
      })

      if (!changed)
        return

      setLoading(true)

      resolveParts({
        offer: offer,
        packageItemsIds: parts.map(x => x.packageItem!.packageItemId!) ?? []
      })
        .then(({ data: updatedOffer }) => {
          updateOffer(updatedOffer) // useEffect na changed zajisti zavolani saveOfferParts a nasledne rekalkulaci
        })
        .finally(() =>
          setTimeout(() => setLoading(false), 100)) // aby loading zmizel az po renderu       
    })
  }

  const itemPerGroupKey = Object.keys(insuranceGroups ?? {})
    .map(key => {
      const items = insuranceGroups[key]
        .filter(
          (x) =>
            ![
              Risk.RESPONSIBILITY,
              Risk.RESPONSIBILITY_REALESTATE,
              Risk.SPOL_PROPERTY,
              Risk.SPOL_FLOOD,
              Risk.SPOL_RESPONSIBILITY,
            ].includes(x.id),
        )

      return { items, key }
    })
    .filter(x => x.items.length > 0)

  return (
    <Box sx={{ display: 'grid', gap: 'px', alignSelf: 'end', mb: printMode ? '2px' : '1px' }} >
      {itemPerGroupKey && itemPerGroupKey.length > 0 && itemPerGroupKey.map(x => {
        const items = x.items
        const headerCells = items.map(y => {
          return {
            id: y.id.toString(),
            title: y.name,
          }
        })
        const section = items[0]

        let icon: keyof typeof icons | '' = ''
        switch (product) {
          case Product.TravelInsurance:
            icon = section.groupId == PackageItemTypeCategory.Addons
              ? 'extras'
              : 'travelBasic'
            break
          case Product.PropertyInsurance:
          case Product.LiabilityInsurance:
            switch (section.groupId) {
              case PackageItemTypeCategory.Household:
                icon = 'household'
                break
              case PackageItemTypeCategory.Realty:
                icon = 'property'
                break
              default:
                icon = 'common'
                break
            }
            break
        }

        return (
          <OfferPackageColumnSection
            width={250}
            key={section.groupId}
            sectioId={section.groupId}
            title={section.groupName}
            icon={icon}
            isOpened={openedColumns.includes(section.groupId)}
            onClick={() => onSectionTitleClick(section.groupId)}
            headerCells={headerCells}
          />
        )
      })}

      {responsibility &&
        <SectionTitle icon='lifeLiability'>
          <Flex w='100%' justifyContent='space-between' gap={2}>
            <Text sx={{ lineHeight: 5, fontWeight: 'medium', whiteSpace: 'nowrap' }}>{responsibility.name}</Text>

            <Flex w='20px'>
              <Checkbox
                size="sm"
                ml='auto'
                disabled={!responsibilityRiskItem?.addable || isContracted}
                isChecked={!!responsibilityRiskItem?.selected}
                onChange={e => handleToggleAll(!responsibilityRiskItem?.selected, Risk.RESPONSIBILITY)}
              />

            </Flex>
          </Flex>
        </SectionTitle>
      }

      {responsibilityRealestate &&
        <SectionTitle icon='propertyLiability'>
          <Flex w='100%' justifyContent='space-between' gap={2}>
            <Text sx={{ lineHeight: 5, fontWeight: 'medium', whiteSpace: 'nowrap' }}>{responsibilityRealestate.name}</Text>

            <Flex w='20px'>
              <Checkbox
                size="sm"
                ml='auto'
                disabled={!responsibilityRealestateRiskItem?.addable || isContracted}
                isChecked={!!responsibilityRealestateRiskItem?.selected}
                onChange={e => handleToggleAll(!responsibilityRealestateRiskItem?.selected, Risk.RESPONSIBILITY_REALESTATE)}
              />
            </Flex>
          </Flex>
        </SectionTitle>
      }

      {spolProperty &&
        <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', borderColor: 'transparent' }} riskId={Risk.SPOL_PROPERTY}>
          <Text fontWeight='medium'>{spolProperty.name}</Text>
        </OfferCellWrapper>
      }

      {spolFlood &&
        <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', borderColor: 'transparent' }} riskId={Risk.SPOL_FLOOD}>
          <Text fontWeight='medium'>{spolFlood.name}</Text>
        </OfferCellWrapper>
      }

      {spolResponsibility &&
        <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', borderColor: 'transparent' }} riskId={Risk.SPOL_RESPONSIBILITY}>
          <Text fontWeight='medium'>{spolResponsibility.name}</Text>
        </OfferCellWrapper>
      }

      <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', borderColor: 'transparent' }} riskId='discount'>
        <Text fontWeight='medium'>Sleva</Text>
      </OfferCellWrapper>

      {[Product.PropertyInsurance, Product.LiabilityInsurance].includes(product!) &&
        <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', borderColor: 'transparent' }} riskId='payment'>
          <Text fontWeight='medium'>Splátka</Text>
        </OfferCellWrapper>
      }

      <Box height='52px'></Box>
    </Box>
  )
}
